import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Card, Row, Col, Space, Typography, Badge, Spin, Button,
  Modal, Form, InputNumber, Input, Select, Tag, Divider, Switch, message, Tooltip,
} from "antd";
import {
  ClusterOutlined, ReloadOutlined, LockOutlined, UnlockOutlined, PrinterOutlined,
} from "@ant-design/icons";
import {
  DndContext, DragOverlay, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { toast } from "react-toastify";
import { getDefaultFlow } from "../../api/productionApi";
import { getBatchesForKanban, moveBatchStage, setBatchLock } from "../../api/batchPlanningApi";
import BatchCard from "./BatchCard";
import MachineInstructions from "../batch-planning/MachineInstructions";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Production stages that require a completion form when leaving
const PRODUCTION_STAGES = [
  "corrugation", "sheet_cutting", "pasting", "casting",
  "pre_print", "coating", "die_cutting", "joining", "after_box_print", "qc",
];

// Droppable column component
const BatchColumn = ({ id, title, color, items, machineType, onCardClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      style={{
        minWidth: 220, maxWidth: 260, flex: "0 0 240px",
        background: isOver ? "#e6f7ff" : "#f5f5f5",
        borderRadius: 8, borderTop: `3px solid ${color || "#1890ff"}`,
        padding: 8, transition: "background 0.2s",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12 }}>{title}</Text>
        <Badge count={items.length} showZero style={{ backgroundColor: color || "#1890ff" }} />
      </div>
      {machineType && (
        <Tag style={{ fontSize: 10, marginBottom: 6 }}>{machineType}</Tag>
      )}
      <div ref={setNodeRef} style={{ minHeight: 60 }}>
        <SortableContext items={items.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          {items.map((batch) => (
            <BatchCard key={batch.id} item={batch} onCardClick={onCardClick} />
          ))}
        </SortableContext>
        {items.length === 0 && (
          <Text type="secondary" style={{ fontSize: 11, padding: "8px 0", display: "block", textAlign: "center" }}>
            No batches
          </Text>
        )}
      </div>
    </div>
  );
};

const BatchKanban = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");
  const navigate = useNavigate();

  const [stages, setStages] = useState([]);
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [dragFromContainer, setDragFromContainer] = useState(null);

  // Stage completion modal
  const [stageFormVisible, setStageFormVisible] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [stageForm] = Form.useForm();

  // Machine instructions modal
  const [machineInstOpen, setMachineInstOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [flow, kanbanData] = await Promise.all([
        getDefaultFlow(),
        getBatchesForKanban(),
      ]);

      const flowStages = (flow.stages || []).sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      setStages(flowStages);

      const cols = {};
      flowStages.forEach((s) => {
        cols[s.code] = kanbanData[s.code] || [];
      });
      setColumns(cols);
    } catch (err) {
      toast.error("Failed to load batch kanban");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (!isLogged) return <Navigate to="/auth/login" replace />;
  checkTokenExp(accessToken);

  const findContainer = (id) => {
    if (id in columns) return id;
    for (const [code, batches] of Object.entries(columns)) {
      if (batches.find((b) => b.id === id)) return code;
    }
    return null;
  };

  const handleDragStart = (event) => {
    const container = findContainer(event.active.id);
    setDragFromContainer(container);
    if (container) {
      const batch = columns[container].find((b) => b.id === event.active.id);
      setActiveItem(batch || null);
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id) || over.id;
    if (!from || !to || from === to) return;
    setColumns((prev) => {
      const fromBatches = [...(prev[from] || [])];
      const toBatches = [...(prev[to] || [])];
      const idx = fromBatches.findIndex((b) => b.id === active.id);
      if (idx === -1) return prev;
      const [moved] = fromBatches.splice(idx, 1);
      return { ...prev, [from]: fromBatches, [to]: [...toBatches, moved] };
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveItem(null);
    const from = dragFromContainer;
    setDragFromContainer(null);
    if (!over || !from) return;

    const to = findContainer(over.id) || over.id;
    if (from === to) {
      const batches = columns[from];
      const oldIdx = batches.findIndex((b) => b.id === active.id);
      const newIdx = batches.findIndex((b) => b.id === over.id);
      if (oldIdx !== -1 && newIdx !== -1 && oldIdx !== newIdx) {
        setColumns((prev) => ({ ...prev, [from]: arrayMove(prev[from], oldIdx, newIdx) }));
      }
      return;
    }

    const batch = columns[to]?.find((b) => b.id === active.id);
    if (!batch) return;

    const fromStage = stages.find((s) => s.code === from);

    // Production stages require a completion form
    if (PRODUCTION_STAGES.includes(from)) {
      const targetStage = stages.find((s) => s.code === to);
      setPendingMove({ batch, from, to, fromStage, targetStage });
      stageForm.resetFields();
      stageForm.setFieldsValue({ output_qty: batch.total_quantity || 0 });
      setStageFormVisible(true);
      return;
    }

    // Non-production move
    await doMoveBatch(batch.id, to);
  };

  const doMoveBatch = async (batchId, toStage, completion = null) => {
    try {
      await moveBatchStage(batchId, toStage, completion);
      const stageName = stages.find((s) => s.code === toStage)?.name || toStage;
      toast.success(`Batch moved to ${stageName} — all jobs updated`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to move batch");
      fetchData();
    }
  };

  const handleStageFormSubmit = async () => {
    if (!pendingMove) return;
    try {
      const values = await stageForm.validateFields();
      await doMoveBatch(pendingMove.batch.id, pendingMove.to, {
        output_qty: values.output_qty,
        wastage_qty: values.wastage_qty || 0,
        operator_name: values.operator_name,
        shift: values.shift,
        notes: values.notes,
      });
      setStageFormVisible(false);
      setPendingMove(null);
    } catch (err) {
      if (err?.errorFields) return; // form validation
      toast.error("Failed to complete stage move");
    }
  };

  const handleToggleLock = async (batchId, currentlyLocked, e) => {
    e.stopPropagation();
    try {
      await setBatchLock(batchId, !currentlyLocked);
      message.success(currentlyLocked ? "Batch unlocked — jobs can move independently" : "Batch locked");
      fetchData();
    } catch (err) {
      message.error("Failed to toggle lock");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <PageTitle title="Back" subtitle="Batch Production Kanban" />

      <Card
        title={
          <Space>
            <ClusterOutlined style={{ fontSize: 18 }} />
            <Title level={4} style={{ margin: 0 }}>Batch Kanban</Title>
            <Tag color="blue">Drag batch card → moves all jobs in it</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => setMachineInstOpen(true)}
            >
              Machine Instructions
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {loading && Object.keys(columns).length === 0 ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <Row wrap={false} gutter={8} style={{ flexWrap: "nowrap", paddingBottom: 8 }}>
                {stages.map((stage) => (
                  <Col key={stage.code} flex="0 0 auto">
                    <BatchColumn
                      id={stage.code}
                      title={stage.name}
                      color={stage.color || "#1890ff"}
                      machineType={stage.machine_type}
                      items={(columns[stage.code] || []).map((batch) => ({
                        ...batch,
                        // inject lock toggle as extra prop for card rendering
                        _onToggleLock: (e) => handleToggleLock(batch.id, batch.is_locked, e),
                      }))}
                      onCardClick={(batch) => navigate(`/batch/${batch.id}`)}
                    />
                  </Col>
                ))}
              </Row>

              <DragOverlay>
                {activeItem ? <BatchCard item={activeItem} /> : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </Card>

      {/* Stage Completion Modal */}
      <Modal
        title={
          <Space>
            <ClusterOutlined />
            <span>
              Complete {pendingMove?.fromStage?.name} — Batch {pendingMove?.batch?.batch_number}
            </span>
          </Space>
        }
        open={stageFormVisible}
        onOk={handleStageFormSubmit}
        onCancel={() => { setStageFormVisible(false); setPendingMove(null); fetchData(); }}
        okText="Move Batch"
        width={480}
      >
        {pendingMove && (
          <div>
            <Text type="secondary">
              Moving <Text strong>{pendingMove.batch.batch_number}</Text> ({pendingMove.batch.jobs?.length} jobs) from{" "}
              <Tag>{pendingMove.fromStage?.name}</Tag> to{" "}
              <Tag color="blue">{pendingMove.targetStage?.name}</Tag>
            </Text>
            <Divider style={{ margin: "12px 0" }} />
          </div>
        )}
        <Form form={stageForm} layout="vertical" size="small">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Output Qty" name="output_qty" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Wastage Qty" name="wastage_qty">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Operator" name="operator_name">
                <Input placeholder="Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Shift" name="shift">
                <Select placeholder="Select shift" allowClear>
                  <Select.Option value="Morning">Morning</Select.Option>
                  <Select.Option value="Evening">Evening</Select.Option>
                  <Select.Option value="Night">Night</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Notes" name="notes">
            <TextArea rows={2} placeholder="Optional notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Machine / Stage Instructions */}
      <MachineInstructions open={machineInstOpen} onClose={() => setMachineInstOpen(false)} />
    </div>
  );
};

export default BatchKanban;
